import { useMemo, useState, type KeyboardEvent } from 'react';
import { scaleBand, scaleLinear } from 'd3-scale';
import type { AnalysisConfig, MetricKey, ReferenceSummary, YearMetrics } from '../../core/domain/types';
import { METRICS, toneHex } from '../../app/metrics';
import { fmt } from '../../app/format';
import { useWidth } from '../hooks';
import { Patterns, Tooltip, niceMax, niceTicks, useTip } from './common';

export type YearStatus = 'vollständig' | 'Teilwert' | 'lädt' | 'keine Daten';

export function yearStatus(m: YearMetrics | undefined): YearStatus {
  if (!m) return 'lädt';
  if (m.complete) return 'vollständig';
  if (!m.loaded && m.daysValid === 0) return 'lädt';
  if (m.daysValid === 0 && m.nightsValid === 0) return 'keine Daten';
  return 'Teilwert';
}

interface Props {
  years: YearMetrics[];
  metric: MetricKey;
  cfg: AnalysisConfig;
  reference: ReferenceSummary | null;
  from: number;
  to: number;
  selectedYear?: number | null;
  onSelectYear?: (y: number) => void;
  height?: number;
  fixedWidth?: number;
  idPrefix?: string;
}

export function YearChart({ years, metric, cfg, reference, from, to, selectedYear, onSelectYear, height = 320, fixedWidth, idPrefix = 'yc' }: Props) {
  const [ref, measured] = useWidth<HTMLDivElement>(800);
  const width = fixedWidth ?? measured;
  const meta = METRICS[metric];
  const color = toneHex(meta.tone);
  const { tip, show, hide } = useTip();
  const [focusYear, setFocusYear] = useState<number | null>(null);
  const isMean = meta.kind === 'mean';
  const narrow = width < 520;

  const data = useMemo(() => {
    const out: { year: number; m?: YearMetrics; v: number | null; status: YearStatus }[] = [];
    for (let y = from; y <= to; y++) {
      const m = years.find((x) => x.year === y);
      const status = yearStatus(m);
      const raw = m ? (m[metric] as number | null) : null;
      out.push({ year: y, m, v: status === 'vollständig' || status === 'Teilwert' ? raw : null, status });
    }
    return out;
  }, [years, metric, from, to]);

  const refMean = reference && reference.status !== 'ungenuegend' ? (reference.means[metric] ?? null) : null;
  const margin = { top: 28, right: narrow ? 12 : 150, bottom: 28, left: narrow ? 40 : 52 };
  const iw = Math.max(80, width - margin.left - margin.right);
  const ih = height - margin.top - margin.bottom;

  const x = scaleBand<number>()
    .domain(data.map((d) => d.year))
    .range([0, iw])
    .paddingInner(data.length > 25 ? 0.22 : 0.3)
    .paddingOuter(0.1);

  const vals = data.map((d) => d.v).filter((v): v is number => v !== null);
  let y;
  if (isMean) {
    const lo = Math.min(...vals, refMean ?? Infinity);
    const hi = Math.max(...vals, refMean ?? -Infinity);
    const pad = Math.max(0.3, (hi - lo) * 0.15);
    y = scaleLinear().domain(vals.length ? [Math.floor((lo - pad) * 2) / 2, Math.ceil((hi + pad) * 2) / 2] : [0, 1]).range([ih, 0]);
  } else {
    y = scaleLinear().domain([0, niceMax(Math.max(...vals, refMean ?? 0, 1))]).range([ih, 0]);
  }
  const ticks = niceTicks(y.domain()[0], y.domain()[1], narrow ? 4 : 5);
  const labelEvery = x.bandwidth() < 14 ? (narrow ? 10 : 5) : x.bandwidth() < 24 ? 5 : 1;

  const refX0 = x(Math.max(from, cfg.referenceStart));
  const refX1 = x(Math.min(to, cfg.referenceEnd));
  const refVisible = refX0 !== undefined && refX1 !== undefined && cfg.referenceStart <= to && cfg.referenceEnd >= from;

  const tipFor = (d: (typeof data)[number]) => {
    const cov = d.m ? meta.coverage(d.m) : null;
    return (
      <>
        <strong>{d.year}</strong>
        <br />
        {meta.label}: {d.v === null ? '–' : `${fmt(d.v, meta.decimals)} ${meta.unit}`}
        <br />
        <span className="tt-status">
          Status: {d.status}
          {d.status === 'Teilwert' && cov ? ` (${fmt(cov.observed)} von ${fmt(cov.expected)} ${cov.unit} gültig)` : ''}
        </span>
        {refMean !== null && d.v !== null && (
          <>
            <br />
            <span className="tt-status">
              Abweichung vom Referenzmittel: {d.v - refMean >= 0 ? '+' : '−'}
              {fmt(Math.abs(d.v - refMean), meta.decimals)} {meta.unit}
            </span>
          </>
        )}
      </>
    );
  };

  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    const cur = focusYear ?? selectedYear ?? to;
    let next = cur;
    if (e.key === 'ArrowRight') next = Math.min(to, cur + 1);
    else if (e.key === 'ArrowLeft') next = Math.max(from, cur - 1);
    else if (e.key === 'Home') next = from;
    else if (e.key === 'End') next = to;
    else if ((e.key === 'Enter' || e.key === ' ') && onSelectYear) {
      e.preventDefault();
      onSelectYear(cur);
      return;
    } else return;
    e.preventDefault();
    setFocusYear(next);
    const d = data.find((q) => q.year === next);
    if (d) show({ x: margin.left + (x(next) ?? 0) + x.bandwidth() / 2, y: margin.top + (d.v !== null ? y(d.v) : ih) - 40, content: tipFor(d) });
  };

  const summary = `${meta.label} ${from}–${to}. ${vals.length} Jahre mit Werten${refMean !== null ? `, Referenzmittel ${fmt(refMean, meta.refDecimals)} ${meta.unit}` : ''}. Pfeiltasten wählen ein Jahr, Eingabetaste öffnet das Tagesdetail.`;

  return (
    <div ref={ref} className="chart" style={{ position: 'relative', width: fixedWidth }}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={summary}
        tabIndex={0}
        onKeyDown={onKey}
        onBlur={() => {
          setFocusYear(null);
          hide();
        }}
      >
        <Patterns id={idPrefix} />
        <g transform={`translate(${margin.left},${margin.top})`}>
          {refVisible && (
            <g>
              <rect x={(refX0 ?? 0) - x.step() * x.paddingInner() * 0.5} y={-margin.top + 6} width={(refX1 ?? 0) - (refX0 ?? 0) + x.bandwidth() + x.step() * x.paddingInner()} height={ih + margin.top - 6} fill="#f3f4f6" />
              <text x={(refX0 ?? 0) + 2} y={-margin.top + 20} fontSize={12} fill="#4b5563">
                Referenzperiode {cfg.referenceStart}–{cfg.referenceEnd}
              </text>
            </g>
          )}
          <g className="axis">
            {ticks.map((t) => (
              <g key={t} transform={`translate(0,${y(t)})`}>
                <line className="gridline" x1={0} x2={iw} />
                <text x={-8} dy="0.32em" textAnchor="end">
                  {fmt(t, isMean ? 1 : 0)}
                </text>
              </g>
            ))}
            <text x={-margin.left + 2} y={-12} fontSize={12} fill="#4b5563">
              {meta.unit}
            </text>
          </g>
          {data.map((d) => {
            const bx = x(d.year) ?? 0;
            const bw = x.bandwidth();
            const active = d.year === selectedYear || d.year === focusYear;
            const common = {
              onMouseMove: (e: React.MouseEvent) => {
                const r = (e.currentTarget as SVGElement).ownerSVGElement!.getBoundingClientRect();
                show({ x: e.clientX - r.left, y: e.clientY - r.top - 50, content: tipFor(d) });
              },
              onMouseLeave: hide,
              onClick: () => onSelectYear?.(d.year),
            };
            if (d.status === 'lädt') {
              return (
                <g key={d.year} {...common} className="bar-hit">
                  <rect x={bx} y={0} width={bw} height={ih} fill="transparent" />
                  <rect x={bx} y={ih - 3} width={bw} height={3} fill="#d1d5db" />
                </g>
              );
            }
            if (d.v === null) {
              return (
                <g key={d.year} {...common} className="bar-hit">
                  <rect x={bx} y={0} width={bw} height={ih} fill={`url(#${idPrefix}-gap)`} opacity={0.6} />
                </g>
              );
            }
            if (isMean) {
              return (
                <g key={d.year} {...common} className="bar-hit">
                  <rect x={bx} y={0} width={bw} height={ih} fill="transparent" />
                  <circle cx={bx + bw / 2} cy={y(d.v)} r={active ? 5 : 3.5} fill={d.status === 'vollständig' ? color : '#fff'} stroke={color} strokeWidth={1.5} />
                </g>
              );
            }
            const top = y(Math.max(0, d.v));
            const h = Math.max(d.v > 0 ? 1 : 0, ih - top);
            return (
              <g key={d.year} {...common} className="bar-hit">
                <rect x={bx} y={0} width={bw} height={ih} fill="transparent" />
                <rect x={bx} y={ih - h} width={bw} height={h} fill={d.status === 'vollständig' ? color : `url(#${idPrefix}-hatch)`} stroke={d.status === 'vollständig' ? 'none' : color} strokeWidth={1} opacity={d.status === 'vollständig' ? (active ? 1 : 0.86) : 1} />
                {active && <rect x={bx - 2} y={ih - h - 2} width={bw + 4} height={h + 4} fill="none" stroke="#0f7377" strokeWidth={2} rx={2} />}
              </g>
            );
          })}
          {isMean && (
            <path
              d={data
                .map((d, i) => {
                  if (d.v === null) return '';
                  const prev = data[i - 1];
                  const cmd = !prev || prev.v === null ? 'M' : 'L';
                  return `${cmd}${(x(d.year) ?? 0) + x.bandwidth() / 2},${y(d.v)}`;
                })
                .join('')}
              fill="none"
              stroke={color}
              strokeWidth={1.4}
              opacity={0.7}
            />
          )}
          {refMean !== null && (
            <g>
              <line x1={0} x2={iw} y1={y(refMean)} y2={y(refMean)} stroke="#1f2937" strokeWidth={1.4} strokeDasharray="6 4" />
              {narrow ? (
                <text className="ref-label" x={iw} y={y(refMean) - 6} textAnchor="end" fill="#1f2937" stroke="#ffffff" strokeWidth={4} strokeLinejoin="round" paintOrder="stroke">
                  Mittel {cfg.referenceStart}–{cfg.referenceEnd}: {fmt(refMean, meta.refDecimals)}
                </text>
              ) : (
                <>
                  <text className="ref-label" x={iw + 10} y={y(refMean) - 3} fill="#1f2937">
                    Mittel {cfg.referenceStart}–{cfg.referenceEnd}
                  </text>
                  <text x={iw + 10} y={y(refMean) + 13} fontSize={12} fill="#1f2937">
                    {fmt(refMean, meta.refDecimals)} {meta.unit}
                    {reference?.status === 'teil' ? ` · Teilreferenz (${reference.yearsValid.length} J.)` : ''}
                  </text>
                </>
              )}
            </g>
          )}
          <g className="axis" transform={`translate(0,${ih})`}>
            <line x1={0} x2={iw} stroke="#1f2937" />
            {data
              .filter((d) => d.year % labelEvery === 0 || (labelEvery === 1 && true))
              .map((d) => (
                <text key={d.year} x={(x(d.year) ?? 0) + x.bandwidth() / 2} y={18} textAnchor="middle">
                  {labelEvery === 1 && x.bandwidth() < 34 ? String(d.year).slice(2) : d.year}
                </text>
              ))}
          </g>
        </g>
      </svg>
      <Tooltip tip={tip} width={width} />
    </div>
  );
}
